import type { Signal } from '@preact/signals-react'
import log from '@susu/log'
import { retry, withTimeout } from '@susu/promise'
import {
  getGA4ClientIdFromCookie,
  getGA4SessionDataFromCookie,
} from '@susu/segment-common'

import { reportBootStage } from './boot-health'
import { getGaCookieId } from './constants'
import { isBrowser } from './env'

let gaLoadedSignal: Signal<boolean | undefined> | undefined

/**
 * Sets the GA loaded signal for tracking Google Analytics initialization state.
 * @param signal - The signal to track GA loaded state
 * @returns void
 */
export const setGALoadedSignal = (
  signal: Signal<boolean | undefined>,
): void => {
  gaLoadedSignal = signal
}

// How long to keep polling GA4's own cookies for a genuinely-established
// session before giving up and proceeding anyway (ad blockers / ITP block GA
// entirely, so this must not hold tracking hostage forever).
const GA_SESSION_READY_TIMEOUT_MS = 2000
const GA_SESSION_POLL_INTERVAL_MS = 100
const GA_SESSION_POLL_ATTEMPTS = Math.ceil(
  GA_SESSION_READY_TIMEOUT_MS / GA_SESSION_POLL_INTERVAL_MS,
)

/**
 * Waits until GA4's own cookies (`_ga` client ID, `_ga_<container-id>` session
 * ID) are genuinely populated, bounded by GA_SESSION_READY_TIMEOUT_MS. gtag
 * writes these cookies asynchronously after loading, so checking immediately
 * after Segment's GA4 destination becomes ready can race — the entrance hit
 * fires before the session cookie exists, which is why it can go out with an
 * empty sid even though gtag is "loaded" (DA-1340). Never rejects — resolves
 * once ready, or after the timeout so tracking is never held hostage.
 */
const waitForGA4Cookies = async (
  gaCookieId: string | undefined,
): Promise<boolean> => {
  try {
    await withTimeout(
      retry(
        async () => {
          const clientId = getGA4ClientIdFromCookie()
          const { sessionId } = getGA4SessionDataFromCookie(gaCookieId ?? '')
          if (!clientId || !sessionId) {
            throw new Error('GA4 cookies not ready yet')
          }
        },
        GA_SESSION_POLL_ATTEMPTS,
        GA_SESSION_POLL_INTERVAL_MS,
      ),
      GA_SESSION_READY_TIMEOUT_MS,
    )
    return true
  } catch {
    // Timed out or GA never loaded — proceed anyway (see doc comment above).
    return false
  }
}

/**
 * Signals GA readiness once its own cookies are genuinely populated (or the
 * readiness margin has passed). Client/session IDs are read live from cookies
 * wherever they're needed (getGA4ClientIdFromCookie / getGA4SessionDataFromCookie
 * in @susu/segment-common) — this only gates the gaLoaded signal so callers
 * don't fire their first event before GA's session actually exists.
 * This function is safe to call in SSR environments and will only execute in the browser.
 */
export const createGAdata = async (): Promise<void> => {
  if (!isBrowser()) {
    return
  }

  const resolved = await waitForGA4Cookies(getGaCookieId())
  reportBootStage('ga-cookies', resolved ? 'ok' : 'timeout')

  if (gaLoadedSignal) {
    gaLoadedSignal.value = true
  }
}

/**
 * Standardized error handler for the analytics-client package.
 * Logs errors consistently across all error scenarios.
 * @param error - The error to handle, can be an Error object or error record
 * @returns void
 */
export const errorHandler = (error: Error | Record<string, unknown>): void => {
  log.error(error)
}

/**
 * Creates a Promise that resolves when a signal's value becomes defined.
 * If the signal already has a defined value, the promise resolves immediately.
 * @template T - The type of the signal value
 * @param signal - The signal or signal-like object to observe
 * @returns Promise that resolves with the signal's value once it's defined
 */
export const createPromiseFromSignal = <T>(
  signal:
    | Signal<T | undefined>
    | { value: T; subscribe?: (fn: (value: T) => void) => () => void },
): Promise<T> => {
  return new Promise<T>(resolve => {
    // Check if value is already set (and not undefined for undefined signals)
    if ('value' in signal && signal.value !== undefined) {
      resolve(signal.value as T)
      return
    }

    // Subscribe to changes
    if ('subscribe' in signal && signal.subscribe) {
      const unsubscribe = signal.subscribe(value => {
        if (value !== undefined) {
          unsubscribe()
          resolve(value as T)
        }
      })
    }
  })
}

/**
 * Validates the application ID parameter.
 * @template ApplicationIdType - The type of the application ID
 * @param applicationId - The application ID to validate
 * @throws {Error} If applicationId is undefined, null, or empty
 * @returns void
 */
export const validateApplicationId = <ApplicationIdType>(
  applicationId: ApplicationIdType,
  allowedApplicationIds?: readonly string[],
): void => {
  if (
    applicationId === undefined ||
    applicationId === null ||
    (typeof applicationId === 'string' && applicationId.trim() === '')
  ) {
    throw new Error('applicationId is required and must be a non-empty value')
  }

  // An id the tracking plan does not recognise still sends — the event is more
  // useful than no event — but it is reported rather than left to be found in
  // the warehouse months later. Five applications were stamping an
  // unrecognised id before this check existed.
  if (
    allowedApplicationIds &&
    allowedApplicationIds.length > 0 &&
    !allowedApplicationIds.includes(String(applicationId))
  ) {
    errorHandler(
      new Error(
        `[analytics-client] applicationId '${String(applicationId)}' is not one of this source's generated ApplicationId values (${allowedApplicationIds.join(', ')}). The event will still send, but the tracking plan will reject it.`,
      ),
    )
  }
}

/**
 * Add a script to the document head, once per URL.
 *
 * No-op when a script with the same `src` already exists anywhere in the
 * document — including a tag another loader has in flight. `attributes` are
 * NOT merged onto the existing tag in that case (FC-198).
 * @param url - The URL of the script to add
 * @param attributes - The attributes to add to the script
 * @example
 * addScript('https://cdn.cookielaw.org/consent/1234567890/OtAutoBlock.js', {
 *   'data-document-language': 'true',
 *   'data-ot-ignore': 'true',
 *   'charSet': 'UTF-8',
 *   'data-domain-script': `1234567890`,
 * })
 * @returns void
 */
export const addScript = (
  url: string,
  attributes: Record<string, unknown> = {},
): void => {
  // Covers a tag already in flight from another loader (GTM, Shopify, a
  // OneTrust admin tag) — those never set the window globals we'd otherwise
  // guard on (FC-198).
  if (window?.document?.querySelector(`script[src="${url}"]`)) {
    return
  }

  const script = window?.document?.createElement('script')
  script.type = 'text/javascript'
  script.src = url

  for (const [key, value] of Object.entries(attributes)) {
    script.setAttribute(key, value as string)
  }

  window?.document?.head.appendChild(script)
}
